import React from "react";
import { SimpleTableDesktopView } from "./SimpleTableDesktopView";
import { SimpleTableContextProvider } from "./contexts/SimpleTableContext";
import { useSimpleTableCallbacks } from "./hooks/useSimpleTableCallbacks";

// Import setup modules
import { useSimpleTableSetup } from "./SimpleTableSetup";
import { extractSimpleTableState } from "./SimpleTableStateSetup";
import { createSimpleTableCallbackWrappers } from "./SimpleTableCallbacksSetup";
import { useSimpleTableContextValue } from "./SimpleTableContextValue/use-context-value";
import { SimpleTableRowServicesProvider } from "./contexts/SimpleTableRowServicesContext";
import { renderSimpleTableConditional } from "./SimpleTableConditionalRenders";
import { useQuickFilterSync } from "./hooks/useQuickFilterSync";

// ============================================================================
// TYPES - Imported from shared types
// ============================================================================

import type { BaseEntity, FieldValue, SimpleTableProps } from "./types";
import type { SimpleTableSetupResult } from "./SimpleTableSetup";
import type { ComposedTableCallbacks } from "./SimpleTableContextValue/callbacks-slice";

/**
 * `data = []` in the destructuring default allocates a NEW array on every
 * render when the caller omits `data`, which alone would invalidate the stable
 * context slice every render. `never[]` is assignable to `TData[]`, so one
 * module-level empty array serves every entity.
 */
const EMPTY_DATA: never[] = [];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SimpleTable<TData extends BaseEntity>({
  config,
  data = EMPTY_DATA,
  isLoading = false,
  error = null,
  onEdit,
  onView,
  onInlineEdit,
  onDelete,
  onCreate,
  onBulkAction,
  savedViewsControls,
  mobileHeaderActions,
  onSavedViewsReady,
  loadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  autoScrollEnabled,
  // Flag to indicate if data is already filtered
  isDataPreFiltered = false,
  // Saved Views (optional)
  savedViews,
  onApplySavedView,
  onSaveView,
  onDeleteSavedView,
  // Export (optional)
  onExport,
  // Pagination (optional)
  currentPage: propCurrentPage,
  totalPages: propTotalPages,
  pageSize: propPageSize,
  totalItems: propTotalItems,
  onPageChange: propOnPageChange,
  onPageSizeChange: propOnPageSizeChange,
  // Entity hooks for actions
  hooks,
  groupingContext,
  groupableFields,
  // Detail modal navigation
  onOpenDetailModal,
  // External filters (mobile list + server queries)
  externalFilters,
  onFiltersChange,
  filters: propTableFilters,
  setFilters: propSetFilters,
  // Hide table body (useful for header-only view in card mode)
  hideTableBody = false,
  // Hide column headers (useful for card view)
  hideColumnHeaders = false,
}: SimpleTableProps<TData>) {
  // ============================================================================
  // SETUP PHASE 1: Initialize all hooks and state
  // ============================================================================

  // The table renders the FULL row set (never a page window) when the caller is
  // in infinite-scroll mode (rows accumulate as the user scrolls) or when
  // grouping is active (rows include group-header entries). In every other case
  // pagination windowing applies so a data source that returns the whole result
  // set — RPC filters, FTS search — still shows one page at a time.
  const disableClientSlice =
    loadMore != null || autoScrollEnabled === true || groupingContext?.isGrouped === true;

  const setup = useSimpleTableSetup<TData>({
    config,
    data,
    propCurrentPage,
    propOnPageChange,
    propPageSize,
    propOnPageSizeChange,
    isDataPreFiltered,
    propTotalItems,
    disableClientSlice,
  });

  // Quick-filter chips must reach the SERVER query, not just the client view.
  useQuickFilterSync<TData>(setup);

  // ============================================================================
  // SETUP PHASE 2: Get callbacks from hook
  // ============================================================================

  const callbacks = useSimpleTableCallbacks<TData>({
    config,
    data,
    state: setup.state,
    appUser: setup.appUser,
    onInlineEdit: onInlineEdit
      ? (payload: Record<string, FieldValue>) => onInlineEdit(payload as unknown as TData)
      : undefined,
    onBulkAction,
  });

  // ============================================================================
  // SETUP PHASE 3: Extract and organize state
  // ============================================================================

  const stateSetup = extractSimpleTableState<TData>(setup);

  // ============================================================================
  // SETUP PHASE 4: Create callback wrappers
  // ============================================================================

  const callbackWrappers = createSimpleTableCallbackWrappers(stateSetup);

  // ============================================================================
  // SETUP PHASE 4.5: Create memoized handlers (MUST be before conditional return)
  // ============================================================================

  // Create handlers that include paginatedData
  const handleSelectAll = React.useMemo(() => {
    const handler = callbacks.createHandleSelectAll();
    return (selected: boolean) => handler(selected, setup.paginatedData);
  }, [callbacks.createHandleSelectAll, setup.paginatedData]);

  const handleBulkAction = React.useMemo(() => {
    const handler = callbacks.createHandleBulkAction();
    return (actionId: string) => handler(actionId, setup.paginatedData);
  }, [callbacks.createHandleBulkAction, setup.paginatedData]);

  // Wrapper for scroll handlers
  const handleBodyScroll = React.useCallback(() => {
    callbacks.handleBodyScroll(setup.tableBodyRef, setup.tableHeaderScrollRef);
  }, [callbacks, setup.tableBodyRef, setup.tableHeaderScrollRef]);

  const handleHeaderScroll = React.useCallback(() => {
    callbacks.handleHeaderScroll(setup.tableBodyRef, setup.tableHeaderScrollRef);
  }, [callbacks, setup.tableBodyRef, setup.tableHeaderScrollRef]);

  // The prop block both the context value and the loading/error/mobile branch
  // read. Declared once so the two stay in step.
  const sharedProps = {
    config,
    data,
    isLoading,
    error,
    onEdit,
    onView,
    onInlineEdit,
    onDelete,
    onCreate,
    onBulkAction,
    savedViewsControls,
    mobileHeaderActions,
    onSavedViewsReady,
    loadMore,
    hasNextPage,
    isFetchingNextPage,
    autoScrollEnabled,
    isDataPreFiltered,
    savedViews,
    onApplySavedView,
    onSaveView,
    onDeleteSavedView,
    onExport,
    currentPage: propCurrentPage,
    totalPages: propTotalPages,
    pageSize: propPageSize,
    totalItems: propTotalItems,
    onPageChange: propOnPageChange,
    onPageSizeChange: propOnPageSizeChange,
    hooks,
    groupingContext,
    groupableFields,
    onOpenDetailModal,
  };

  // The ONE place the context's callback surface is asserted complete. The
  // annotation is load-bearing: `ComposedTableCallbacks` names every callback the
  // context publishes, so omitting one here is a compile error rather than an
  // `undefined` a row discovers when the user clicks its checkbox (#2625).
  const composedCallbacks = React.useMemo<ComposedTableCallbacks<TData>>(
    () => ({
      ...callbacks,
      handleSelectAll,
      handleBulkAction,
      handleBodyScroll,
      handleHeaderScroll,
    }),
    [callbacks, handleSelectAll, handleBulkAction, handleBodyScroll, handleHeaderScroll]
  );

  // ============================================================================
  // SETUP PHASE 5: Build the context value (MUST be before the conditional return)
  // ============================================================================
  //
  // The three slices are memoized independently (see use-context-value.ts): one
  // monolithic, freshly-allocated value meant every SimpleTable render pushed a
  // new context through the provider, which bypasses React.memo and repainted
  // every virtualized row and cell.

  const { stable, volatile, chrome } = useSimpleTableContextValue<TData>(
    setup,
    stateSetup,
    callbackWrappers,
    composedCallbacks,
    { ...sharedProps, hideColumnHeaders, hideTableBody }
  );

  // ============================================================================
  // SETUP PHASE 6: Handle conditional rendering (loading, error, mobile)
  // ============================================================================

  const visibleColumnIdsForMobile = setup.visibleFields.map(([id]) => id);

  const conditionalRender = renderSimpleTableConditional<TData>(
    setup,
    {
      ...sharedProps,
      externalFilters,
      onFiltersChange,
      filters: propTableFilters,
      setFilters: propSetFilters,
      visibleColumnIds: visibleColumnIdsForMobile,
    },
    isLoading,
    error
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  // One update + one delete mutation subscription AND one action role filter
  // for the whole table, instead of two mutations per row in
  // TableRowActionsWrapper, two more in RowContextMenuContent (~140 React Query
  // subscriptions per rendered table) and a `useActionFiltering()` in each of
  // RowContextMenuContentView and useTableRowActions. Mounted only when `hooks`
  // is present so the mutation hooks inside it are never called conditionally.
  //
  // The conditional branch (mobile cards, error, the mount skeleton) rides
  // INSIDE this provider too. It used to early-return above it, so the mobile
  // card path got none of the hoisting: `use-row-action-items` still called
  // `useActionFiltering()` and `hooks.useUpdate()`/`useDelete()` once per CARD
  // (#2629). Cards read the services through the same
  // `useSimpleTableRowServices()` seam the desktop rows do, which falls back to
  // calling the hooks itself wherever no provider is mounted — so this is a
  // subscription change, not a behaviour change.
  const body = conditionalRender ?? <SimpleTableDesktopView />;

  const withRowServices = hooks ? (
    <SimpleTableRowServicesProvider<TData> hooks={hooks}>{body}</SimpleTableRowServicesProvider>
  ) : (
    body
  );

  // The conditional branches render their own chrome and never read the table
  // context, so they stay outside `SimpleTableContextProvider` exactly as before.
  if (conditionalRender) {
    return withRowServices;
  }

  return (
    <SimpleTableContextProvider<TData> stable={stable} volatile={volatile} chrome={chrome}>
      {withRowServices}
    </SimpleTableContextProvider>
  );
}
